import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  bookingPagesAPI,
  eventTypesAPI,
  slotsAPI,
  bookingsAPI,
} from "../services/schedulerAPI";

const PublicBookingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [bookingPage, setBookingPage] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [error, setError] = useState(null);

  // Booking flow state
  const [step, setStep] = useState(1); // 1: Date, 2: Time, 3: Details, 4: Confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Form data
  const [formData, setFormData] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    custom_responses: {},
  });

  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadBookingPage = useCallback(async () => {
    setLoading(true);
    try {
      const pageData = await bookingPagesAPI.getBySlug(slug);
      setBookingPage(pageData);

      // Load event type details
      const eventTypeData = await eventTypesAPI.get(pageData.event_type_id);
      setEventType(eventTypeData);
    } catch (err) {
      setError(
        err.response?.data?.detail || "Booking page not found or inactive"
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadAvailableSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const slotsData = await slotsAPI.getAvailableSlots(
        bookingPage.id,
        dateStr,
        timezone
      );
      setAvailableSlots(slotsData.slots);
    } catch (err) {
      console.error("Failed to load slots:", err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [bookingPage, selectedDate, timezone]);

  useEffect(() => {
    loadBookingPage();
  }, [loadBookingPage]);

  useEffect(() => {
    if (selectedDate && bookingPage) {
      loadAvailableSlots();
    }
  }, [selectedDate, bookingPage, loadAvailableSlots]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const bookingData = {
        booking_page_id: bookingPage.id,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone || null,
        start_time: selectedSlot.start_time,
        timezone: timezone,
        custom_responses: formData.custom_responses,
      };

      const createdBooking = await bookingsAPI.create(bookingData);
      setBooking(createdBooking);
      setStep(4);

      // Redirect if URL specified
      if (bookingPage.redirect_url) {
        setTimeout(() => {
          window.location.href = bookingPage.redirect_url;
        }, 3000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Generate calendar dates for current month
  const generateCalendarDates = () => {
    const today = new Date();
    const dates = [];

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading booking page...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="apple-glass-strong p-12 text-center max-w-md apple-scale-in"
        >
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Page Not Found
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="apple-btn-primary"
          >
            Go Home
          </button>
        </motion.div>
      </div>
    );
  }

  // Confirmation step
  if (step === 4 && booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="apple-glass-strong p-12 text-center max-w-2xl apple-scale-in"
        >
          <div className="text-7xl mb-6">🎉</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Booking Confirmed!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Your meeting has been successfully scheduled
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left space-y-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">Event</div>
              <div className="text-lg font-semibold text-gray-900">
                {eventType.name}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Date & Time</div>
              <div className="text-lg font-semibold text-gray-900">
                {new Date(booking.start_time).toLocaleString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  timeZone: timezone,
                })}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Duration</div>
              <div className="text-lg font-semibold text-gray-900">
                {eventType.duration_minutes} minutes
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Guest</div>
              <div className="text-lg font-semibold text-gray-900">
                {booking.guest_name}
              </div>
              <div className="text-sm text-gray-600">{booking.guest_email}</div>
            </div>

            {bookingPage.location_type === 'google_meet' && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Location</div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">📆</span>
                  <div className="text-sm text-gray-900">
                    Google Meet link will be sent via email
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            A confirmation email with meeting details has been sent to {booking.guest_email}
          </p>

          {bookingPage.redirect_url && (
            <p className="text-sm text-gray-500">
              Redirecting in 3 seconds...
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {bookingPage.title}
          </h1>
          {bookingPage.description && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {bookingPage.description}
            </p>
          )}
          <div className="mt-6 inline-flex items-center gap-2 apple-glass px-6 py-3">
            <span className="text-2xl">🕐</span>
            <span className="font-semibold text-gray-900">
              {eventType.name}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-600">
              {eventType.duration_minutes} min
            </span>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: "Date" },
              { num: 2, label: "Time" },
              { num: 3, label: "Details" },
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div
                  className={`flex items-center gap-2 ${
                    step >= s.num ? "text-primary-600" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      step >= s.num
                        ? "bg-primary-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {s.num}
                  </div>
                  <span className="font-medium text-sm">{s.label}</span>
                </div>
                {i < 2 && (
                  <div
                    className={`h-0.5 w-16 ${
                      step > s.num ? "bg-primary-500" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Select Date */}
            {step === 1 && (
              <motion.div
                key="date"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="apple-glass-strong p-8 apple-slide-up"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Select a Date
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {generateCalendarDates().map((date) => (
                    <button
                      key={date.toISOString()}
                      onClick={() => handleDateSelect(date)}
                      className={`apple-card p-4 apple-hover-lift transition-all ${
                        selectedDate?.toDateString() === date.toDateString()
                          ? "border-primary-500 bg-primary-50"
                          : ""
                      }`}
                    >
                      <div className="text-sm text-gray-600">
                        {date.toLocaleDateString("en-US", { weekday: "short" })}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {date.getDate()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {date.toLocaleDateString("en-US", { month: "short" })}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Select Time Slot */}
            {step === 2 && (
              <motion.div
                key="time"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="apple-glass-strong p-8 apple-slide-up"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Select a Time
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {formatDate(selectedDate)}
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    ← Back
                  </button>
                </div>

                {loadingSlots ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                    <p className="text-gray-600 mt-4">
                      Loading available slots...
                    </p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-lg mb-2">No slots available</p>
                    <p className="text-sm">Please select a different date</p>
                    <button
                      onClick={() => setStep(1)}
                      className="mt-4 apple-btn-primary"
                    >
                      Choose Another Date
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {availableSlots.map((slot, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSlotSelect(slot)}
                        className="apple-card p-4 apple-hover-lift transition-all font-semibold text-gray-900"
                      >
                        {slot.start_time_local}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Enter Details */}
            {step === 3 && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="apple-glass-strong p-8 apple-slide-up"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Enter Your Details
                  </h2>
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    ← Back
                  </button>
                </div>

                {/* Selected Date/Time Summary */}
                <div className="apple-glass p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📅</span>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {formatDate(selectedDate)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedSlot.start_time_local} - {selectedSlot.end_time_local}
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmitBooking} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.guest_name}
                      onChange={(e) =>
                        setFormData({ ...formData, guest_name: e.target.value })
                      }
                      className="apple-input w-full"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.guest_email}
                      onChange={(e) =>
                        setFormData({ ...formData, guest_email: e.target.value })
                      }
                      className="apple-input w-full"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.guest_phone}
                      onChange={(e) =>
                        setFormData({ ...formData, guest_phone: e.target.value })
                      }
                      className="apple-input w-full"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  {/* Custom Questions */}
                  {bookingPage.custom_questions?.map((question) => (
                    <div key={question.id}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {question.label}
                        {question.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      {question.type === "textarea" ? (
                        <textarea
                          required={question.required}
                          value={formData.custom_responses[question.id] || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              custom_responses: {
                                ...formData.custom_responses,
                                [question.id]: e.target.value,
                              },
                            })
                          }
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={question.placeholder}
                        />
                      ) : (
                        <input
                          type={question.type}
                          required={question.required}
                          value={formData.custom_responses[question.id] || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              custom_responses: {
                                ...formData.custom_responses,
                                [question.id]: e.target.value,
                              },
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={question.placeholder}
                        />
                      )}
                    </div>
                  ))}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full apple-btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                    style={{ padding: '16px' }}
                  >
                    {submitting ? "Scheduling..." : "Confirm Booking"}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;
